import { useMemo, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import NsContent from 'components/common/ns-content/NsContent';

import { NsTitle } from 'components/common/ns-title/NsTitle';
import {
  setAccountField,
  selectDetails as selectAccountDetails,
  selectErrors as selectEmailVerificationErrors,
  selectIsLoading as selectEmailVerificationInProgress,
  slice as createAccountSlice
} from 'state/account/account-slice';
import { useValidateEmailThunk } from 'hooks/useEmailValidationThunk';
import { getPhoneType } from 'utils/fieldValidations';
import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { CreateAccountForm } from './components/CreateAccountForm';
import { AddressInfo } from './components/AddressInfo';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsStopgap } from 'components/common/ns-stopgap/NsStopgap';
import { useNavigation } from 'hooks/useNavigation';
import {
  AbandonedCartUpdateStep,
  useAbandonedCartUpdate
} from 'hooks/useAbandonedCart/useAbandonedCartUpdate';
import { patchAbandonedCartThunk } from 'state/abandoned-cart/abandoned-cart-thunks';
import { selectIsLoadingPatch } from 'state/abandoned-cart/abandoned-cart-slice';
import { useAppDispatch } from 'hooks/useAppDispatch';

export const testId = 'createAccountContainerTestId';

export const CreateAccount = () => {
  const dispatch = useAppDispatch();
  const {
    isAbandonedCartAvailable,
    payload,
    encryptedQuoteId
  } = useAbandonedCartUpdate(AbandonedCartUpdateStep.PERSONAL_DETAILS);

  const account = useSelector(selectAccountDetails, shallowEqual);
  const { email } = account;
  const emailVerificationErrors = useSelector(
    selectEmailVerificationErrors,
    shallowEqual
  );
  const emailVerificationInProgress = useSelector(
    selectEmailVerificationInProgress,
    shallowEqual
  );
  const isLoadingAbandonedCart = useSelector(
    selectIsLoadingPatch,
    shallowEqual
  );
  const validateEmailThunk = useValidateEmailThunk(createAccountSlice);
  const { eventTypes, next } = useNavigation();

  const handleGoNext = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        !!account.email && (await dispatch(validateEmailThunk(email)));
        !!isAbandonedCartAvailable &&
          (await dispatch(patchAbandonedCartThunk(payload, encryptedQuoteId)));

        next({ eventType: eventTypes.NEXT });
      } catch (e) {
        e && console.error(e);
      }
    },
    [
      account.email,
      dispatch,
      validateEmailThunk,
      email,
      next,
      eventTypes.NEXT,
      isAbandonedCartAvailable,
      payload,
      encryptedQuoteId
    ]
  );

  const handleChange = useCallback(
    (event) => {
      const {
        target: { name, processedValue: value }
      } = event;

      dispatch(setAccountField({ value, name }));
      if (name === 'phoneNumber') {
        dispatch(
          setAccountField({ name: 'phoneType', value: getPhoneType(value) })
        );
      }
    },
    [dispatch]
  );

  return useMemo(() => {
    if (emailVerificationInProgress || isLoadingAbandonedCart) {
      return <NsStopgap />;
    }

    return (
      <NsPanel testId={testId}>
        <NsBackCta />
        <NsContent>
          <NsTitle title="Your details" />
        </NsContent>
        <AddressInfo />
        <CreateAccountForm
          account={account}
          handleChange={handleChange}
          handleGoNext={handleGoNext}
          apiErrors={emailVerificationErrors}
          isLoading={emailVerificationInProgress}
        />
      </NsPanel>
    );
  }, [
    handleGoNext,
    handleChange,
    account,
    emailVerificationErrors,
    emailVerificationInProgress,
    isLoadingAbandonedCart
  ]);
};

export default CreateAccount;
